import { createStackNavigator } from "@react-navigation/stack"
import { useTheme } from "capsule"
import { Formik } from "formik"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet } from "react-native"

import { NewClientOnboardingNS } from "../../features/i18n/constants"
import { NewClientOnboardingParamList } from "../../features/Navigation/types"
import { getNewClientDocId, updateNewClientData } from "../../features/services/NewClientService"
import NewClientOrganSelectScreen from "./NewClientOrganSelectScreen"
import NewClientPathologyScreen from "./NewClientPathologyScreen"
import NewClientPatientConfirmationScreen from "./NewClientPatientConfirmationScreen"
import NewClientPatientPathologyIntroScreen from "./NewClientPatientPathologyIntroScreen"
import NewClientPatientRecommendationScreen from "./NewClientPatientRecommendationScreen"
import NewClientPatientThankYouScreen from "./NewClientPatientThankYouScreen"
import NewClientProfessionalSelectScreen from "./NewClientProfessionalSelectScreen"
import NewClientProfessionalThankYouScreen from "./NewClientProfessionalThankYouScreen"
import NewClientSelectProfileScreen from "./NewClientSelectProfileScreen"

const newClientStack = createStackNavigator<NewClientOnboardingParamList>()

export const NewClientNavigator = () => {
  const { t } = useTranslation(NewClientOnboardingNS)
  const {
    colors: {
      black: { highEmphasis },
      surface: { background },
    },
    typography: { h6 },
  } = useTheme()

  const commonHeaderProps = useMemo(
    () => ({
      gestureEnabled: false,
      headerBackTitleVisible: false,
      headerTitleStyle: { ...h6, color: highEmphasis },
      headerTintColor: highEmphasis,
      headerStyle: [styles.header, { backgroundColor: background }],
      headerTitleAlign: "left" as const,
    }),
    [highEmphasis, h6, background],
  )

  return (
    <Formik
      initialValues={{
        organ: "",
        pathology: "",
        specialty: "ortho",
        profession: "",
      }}
      onSubmit={async values => {
        const docId = await getNewClientDocId()
        await updateNewClientData(docId, {
          pathology: values.pathology,
        })
      }}
    >
      <newClientStack.Navigator
        screenOptions={commonHeaderProps}
        initialRouteName="NewClientSelectProfile"
      >
        <newClientStack.Screen
          name="NewClientSelectProfile"
          component={NewClientSelectProfileScreen}
          options={{ title: t("selectProfile.title") }}
        />

        <newClientStack.Screen
          name="NewClientPatientConfirmation"
          component={NewClientPatientConfirmationScreen}
          options={{ headerShown: false }}
        />
        <newClientStack.Screen
          name="NewClientPatientRecommendation"
          component={NewClientPatientRecommendationScreen}
          options={{ title: t("patientRecommendation.title") }}
        />
        <newClientStack.Screen
          name="NewClientPatientPathologyIntro"
          component={NewClientPatientPathologyIntroScreen}
          options={{ title: t("organ.title") }}
        />
        <newClientStack.Screen
          name="NewClientOrganSelect"
          component={NewClientOrganSelectScreen}
          options={{ title: t("organ.title") }}
        />

        <newClientStack.Screen
          name="NewClientPathology"
          component={NewClientPathologyScreen}
          options={{ title: t("pathology.title") }}
        />
        <newClientStack.Screen
          name="NewClientPatientThankYou"
          component={NewClientPatientThankYouScreen}
          options={{ headerShown: false }}
        />

        <newClientStack.Screen
          name="NewClientProfessionalSelect"
          component={NewClientProfessionalSelectScreen}
          options={{ title: t("professionalSelect.title") }}
        />
        <newClientStack.Screen
          name="NewClientProfessionalThankYou"
          component={NewClientProfessionalThankYouScreen}
          options={{ headerShown: false }}
        />
      </newClientStack.Navigator>
    </Formik>
  )
}

const styles = StyleSheet.create({
  header: {
    elevation: 0,
    shadowOpacity: 0,
    borderBottomWidth: 0,
  },
})
